import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DevToolsService } from '../dev-tools.service';
import { DevToolsEvent } from '../dev-tools.types';
import { JsonTreeComponent } from './json-tree.component';

interface HttpEventView {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  path: string;
  status: number;
  outcome: 'ok' | 'error' | 'cancelled';
  durationMs: number;
  level: DevToolsEvent['level'];
  payload: unknown;
}

/**
 * Onglet "HTTP" — trace en temps réel toutes les requêtes HttpClient de l'app,
 * sur toutes les pages, via devToolsHttpInterceptor. S'appuie directement sur
 * le signal `events` du DevToolsService : aucune dépendance au snapshot ni au
 * contexte d'une page particulière.
 */
@Component({
  selector: 'dev-http-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, JsonTreeComponent],
  template: `
    <div class="tw-flex tw-flex-col tw-h-full tw-text-gray-100">
      <!-- Toolbar -->
      <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-b tw-border-gray-700/60 tw-bg-gray-900/60 tw-shrink-0">
        <div class="tw-relative tw-flex-1">
          <i class="ph ph-magnifying-glass tw-absolute tw-left-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500 tw-text-[12px]"></i>
          <input
            type="text"
            [value]="search()"
            (input)="onSearch($event)"
            placeholder="Filtrer par URL…"
            class="tw-w-full tw-bg-gray-800/70 tw-border tw-border-gray-700 tw-rounded tw-pl-7 tw-pr-2 tw-py-1 tw-text-[11px] tw-text-gray-200 placeholder:tw-text-gray-500 focus:tw-outline-none focus:tw-border-success-500"
          />
        </div>

        <select
          [value]="methodFilter()"
          (change)="onMethodFilter($event)"
          class="tw-bg-gray-800/70 tw-border tw-border-gray-700 tw-rounded tw-px-2 tw-py-1 tw-text-[11px] tw-text-gray-200 focus:tw-outline-none"
        >
          <option value="all">Méthode</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </select>

        <label class="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-400 tw-cursor-pointer tw-select-none">
          <input
            type="checkbox"
            [checked]="errorsOnly()"
            (change)="onErrorsOnly($event)"
            class="tw-accent-success-500"
          />
          Erreurs
        </label>

        <button
          type="button"
          class="tw-text-[11px] tw-px-2 tw-py-1 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-text-gray-200 tw-border tw-border-gray-700 tw-flex tw-items-center tw-gap-1"
          (click)="clear()"
          title="Vider la liste des requêtes"
        >
          <i class="ph ph-trash tw-text-[12px]"></i> Clear
        </button>
      </div>

      <!-- Counters -->
      <div class="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-1.5 tw-text-[10px] tw-text-gray-500 tw-border-b tw-border-gray-800 tw-shrink-0 tw-bg-gray-900/40">
        <span>{{ filtered().length }} / {{ all().length }} requêtes</span>
        <span class="tw-text-alert-500">{{ errorCount() }} erreur(s)</span>
        <span class="tw-ml-auto tw-italic">buffer max : 500 événements</span>
      </div>

      <!-- Request list -->
      <div class="tw-flex-1 tw-overflow-auto">
        <div class="tw-divide-y tw-divide-gray-800">
          @for (ev of filtered(); track ev.id) {
            <div class="tw-px-2 tw-py-1">
              <button
                type="button"
                class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-text-left hover:tw-bg-gray-800/40 tw-rounded tw-px-1 tw-py-0.5"
                (click)="toggleDetail(ev.id)"
              >
                <i class="ph tw-text-[11px] tw-text-gray-400"
                   [ngClass]="isOpen(ev.id) ? 'ph-caret-down' : 'ph-caret-right'"></i>
                <span class="tw-text-[9px] tw-w-[46px] tw-text-center tw-px-1 tw-py-0.5 tw-rounded tw-font-mono tw-font-bold tw-shrink-0"
                      [ngClass]="methodClass(ev.method)">{{ ev.method }}</span>
                <span class="tw-text-[9px] tw-w-[30px] tw-text-center tw-font-mono tw-font-bold tw-shrink-0"
                      [ngClass]="statusClass(ev)">
                  {{ ev.outcome === 'cancelled' ? '⊘' : ev.status }}
                </span>
                <span class="tw-text-[11px] tw-text-gray-200 tw-font-mono tw-truncate" [title]="ev.url">{{ ev.path }}</span>
                <span class="tw-ml-auto tw-text-[9px] tw-text-gray-500 tw-font-mono tw-shrink-0">{{ ev.durationMs }} ms</span>
                <span class="tw-text-[9px] tw-text-gray-500 tw-font-mono tw-shrink-0">{{ timeLabel(ev.timestamp) }}</span>
              </button>
              @if (isOpen(ev.id)) {
                <div class="tw-pl-6 tw-pt-1 tw-pb-1 tw-overflow-x-auto">
                  <dev-json-tree [value]="ev.payload" [search]="search()" [defaultExpanded]="true" />
                </div>
              }
            </div>
          } @empty {
            <div class="tw-text-[11px] tw-text-gray-500 tw-italic tw-px-3 tw-py-4 tw-text-center">
              Aucune requête capturée pour l'instant. Navigue dans l'app : chaque appel
              HttpClient apparaîtra ici en temps réel, quelle que soit la page.
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class DevToolsHttpTabComponent {
  private readonly devTools = inject(DevToolsService);

  readonly search = signal<string>('');
  readonly methodFilter = signal<string>('all');
  readonly errorsOnly = signal<boolean>(false);
  private readonly openIds = signal<Set<string>>(new Set());

  /** Tous les events HTTP, plus récents en premier. Réactif : suit le signal events. */
  readonly all = computed<HttpEventView[]>(() =>
    this.devTools
      .events()
      .filter((e) => e.source === 'http')
      .map((e) => this.toView(e))
      .reverse()
  );

  readonly filtered = computed<HttpEventView[]>(() => {
    const term = this.search().trim().toLowerCase();
    const method = this.methodFilter();
    const errors = this.errorsOnly();
    return this.all().filter((e) => {
      if (method !== 'all' && e.method !== method) return false;
      if (errors && e.level !== 'error' && e.level !== 'warn') return false;
      if (term && !e.url.toLowerCase().includes(term)) return false;
      return true;
    });
  });

  readonly errorCount = computed(
    () => this.all().filter((e) => e.level === 'error').length
  );

  private toView(e: DevToolsEvent): HttpEventView {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const url = typeof p['url'] === 'string' ? (p['url'] as string) : e.type;
    return {
      id: e.id,
      timestamp: e.timestamp,
      method: typeof p['method'] === 'string' ? (p['method'] as string) : '?',
      url,
      path: this.pathOf(url),
      status: typeof p['status'] === 'number' ? (p['status'] as number) : 0,
      outcome: (p['outcome'] as HttpEventView['outcome']) ?? 'ok',
      durationMs: typeof p['durationMs'] === 'number' ? (p['durationMs'] as number) : 0,
      level: e.level,
      payload: e.payload,
    };
  }

  private pathOf(url: string): string {
    try {
      const u = new URL(url, 'http://x');
      return u.pathname + u.search;
    } catch {
      return url;
    }
  }

  onSearch(ev: Event): void {
    this.search.set((ev.target as HTMLInputElement).value);
  }

  onMethodFilter(ev: Event): void {
    this.methodFilter.set((ev.target as HTMLSelectElement).value);
  }

  onErrorsOnly(ev: Event): void {
    this.errorsOnly.set((ev.target as HTMLInputElement).checked);
  }

  clear(): void {
    this.devTools.clearEvents();
    this.openIds.set(new Set());
  }

  toggleDetail(id: string): void {
    const next = new Set(this.openIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.openIds.set(next);
  }

  isOpen(id: string): boolean {
    return this.openIds().has(id);
  }

  timeLabel(ts: number): string {
    try {
      return new Date(ts).toLocaleTimeString();
    } catch {
      return String(ts);
    }
  }

  methodClass(method: string): string {
    switch (method) {
      case 'GET':
        return 'tw-bg-success-500/15 tw-text-success-500';
      case 'POST':
        return 'tw-bg-info-500/15 tw-text-info-500';
      case 'PUT':
      case 'PATCH':
        return 'tw-bg-warning-500/15 tw-text-warning-500';
      case 'DELETE':
        return 'tw-bg-alert-500/15 tw-text-alert-500';
      default:
        return 'tw-bg-gray-700/40 tw-text-gray-300';
    }
  }

  statusClass(ev: HttpEventView): string {
    if (ev.outcome === 'cancelled') return 'tw-text-gray-500';
    if (ev.level === 'error') return 'tw-text-alert-500';
    if (ev.level === 'warn') return 'tw-text-warning-500';
    return 'tw-text-success-500';
  }
}
